import { AdminNavigation } from "@/components/AdminNavigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarDays, FileText, Zap, BarChart3, Users, HardDrive, Shield, Clock, AlertCircle, CheckCircle2, History, Download, BookOpen, Archive } from "lucide-react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export default function AdminDashboard() {
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Load completed tasks from localStorage
  const [completedTasks, setCompletedTasks] = useState<{[key: string]: {completed: boolean, date?: string}}>(() => {
    const saved = localStorage.getItem('iso27001-completed-tasks');
    return saved ? JSON.parse(saved) : {};
  });

  const toggleTaskCompletion = (taskTitle: string) => {
    const newCompleted = {
      ...completedTasks,
      [taskTitle]: {
        completed: !completedTasks[taskTitle]?.completed,
        date: !completedTasks[taskTitle]?.completed ? new Date().toISOString() : undefined
      }
    };
    setCompletedTasks(newCompleted);
    localStorage.setItem('iso27001-completed-tasks', JSON.stringify(newCompleted));
  };

  const iso27001Tasks = [
    {
      title: "Jährliches Management Review",
      description: "Überprüfung des ISMS durch die Geschäftsführung",
      frequency: "Jährlich",
      month: "Dezember",
      priority: "high",
      dueThisQuarter: currentMonth === 12
    },
    {
      title: "Internes Audit",
      description: "Überprüfung aller ISO 27001 Prozesse und Kontrollen",
      frequency: "Jährlich",
      month: "Oktober",
      priority: "high",
      dueThisQuarter: currentMonth >= 10 && currentMonth <= 12
    },
    {
      title: "Risikobewertung aktualisieren",
      description: "Bewertung neuer Risiken und Überprüfung bestehender",
      frequency: "Halbjährlich",
      month: "Juni & Dezember",
      priority: "high",
      dueThisQuarter: currentMonth === 6 || currentMonth === 12
    },
    {
      title: "Informationssicherheits-Schulung",
      description: "Schulung aller Mitarbeiter zu Sicherheitsrichtlinien",
      frequency: "Jährlich",
      month: "März",
      priority: "medium",
      dueThisQuarter: currentMonth >= 1 && currentMonth <= 3
    },
    {
      title: "Backup-Tests durchführen",
      description: "Wiederherstellung aus Backup testen",
      frequency: "Quartalsweise",
      month: "Laufend",
      priority: "high",
      dueThisQuarter: true
    },
    {
      title: "Lieferanten-Review",
      description: "Überprüfung der Sicherheitsmaßnahmen von Dienstleistern",
      frequency: "Jährlich",
      month: "September",
      priority: "medium",
      dueThisQuarter: currentMonth >= 7 && currentMonth <= 9
    },
    {
      title: "Incident Response Test",
      description: "Simulation eines Sicherheitsvorfalls",
      frequency: "Jährlich",
      month: "Mai",
      priority: "medium",
      dueThisQuarter: currentMonth >= 4 && currentMonth <= 6
    },
    {
      title: "Dokumentation aktualisieren",
      description: "Richtlinien, Verfahren und Formulare überprüfen",
      frequency: "Halbjährlich",
      month: "März & September",
      priority: "medium",
      dueThisQuarter: (currentMonth >= 1 && currentMonth <= 3) || (currentMonth >= 7 && currentMonth <= 9)
    },
    {
      title: "Zugriffsrechte überprüfen",
      description: "Review aller User-Berechtigungen und Zugriffe",
      frequency: "Quartalsweise",
      month: "Laufend",
      priority: "high",
      dueThisQuarter: true
    },
    {
      title: "Physische Sicherheit prüfen",
      description: "Kontrolle von Zutritt, Schließsystemen, Alarmanlagen",
      frequency: "Halbjährlich",
      month: "April & Oktober",
      priority: "low",
      dueThisQuarter: (currentMonth >= 4 && currentMonth <= 6) || (currentMonth >= 10 && currentMonth <= 12)
    }
  ];

  const upcomingTasks = iso27001Tasks.filter(task => task.dueThisQuarter);
  const highPriorityTasks = upcomingTasks.filter(task => task.priority === "high");
  
  const completedHistory = Object.entries(completedTasks)
    .filter(([_, data]) => data.completed && data.date)
    .map(([title, data]) => ({
      title,
      date: new Date(data.date!)
    }))
    .sort((a, b) => b.date.getTime() - a.date.getTime());
  const adminSections = [
    {
      to: "/admin/kalender",
      title: "Kalender",
      description: "Termine verwalten und exportieren",
      icon: CalendarDays,
    },
    {
      to: "/admin/documents",
      title: "Dokumente",
      description: "Dokumente hochladen und verwalten",
      icon: FileText,
    },
    {
      to: "/admin/betroffenenrechte",
      title: "Betroffenenrechte",
      description: "DSGVO-Betroffenenrechte verwalten (Art. 15-22)",
      icon: Users,
    },
    {
      to: "/admin/backup",
      title: "Backup & Wiederherstellung",
      description: "Datensicherung und DSGVO-Compliance",
      icon: HardDrive,
    },
    {
      to: "/admin/performance",
      title: "Performance",
      description: "Optimierungen und Lazy Loading",
      icon: Zap,
    },
    {
      to: "/admin/downloads",
      title: "Downloads & Conversions",
      description: "Downloads und daraus entstandene Anfragen/Buchungen",
      icon: Download,
    },
    {
      to: "/admin/rechtstexte",
      title: "Rechtstexte & Archiv",
      description: "Impressum, Datenschutz, AGB (Stand August 2026) inkl. archivierter Vorversionen",
      icon: Archive,
    },
    {
      to: "/admin/handbuch",
      title: "Handbuch",
      description: "Funktionen der Homepage & technische Dokumentation (inkl. PDF)",
      icon: BookOpen,
    },
    {
      to: "/admin/tracking",
      title: "Tracking",
      description: "Analytics und Marketing Tools",
      icon: BarChart3,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Willkommen im Admin-Bereich. Wählen Sie einen Bereich aus.
          </p>
        </div>

        {/* ISO 27001 Wichtige Termine */}
        <Card className="mb-8 border-2 border-primary/50">
          <CardHeader>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-primary" />
                <div>
                  <CardTitle className="text-2xl">ISO 27001 Compliance-Termine</CardTitle>
                  <CardDescription>
                    Wichtige anstehende Aufgaben (Q{currentQuarter} {new Date().getFullYear()})
                  </CardDescription>
                </div>
              </div>
              {highPriorityTasks.length > 0 && (
                <Badge variant="destructive" className="text-sm">
                  {highPriorityTasks.length} Hohe Priorität
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="upcoming" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="upcoming">Anstehend ({upcomingTasks.length})</TabsTrigger>
                <TabsTrigger value="history">
                  <History className="w-4 h-4 mr-2" />
                  Historie ({completedHistory.length})
                </TabsTrigger>
              </TabsList>

              {/* Anstehende Termine */}
              <TabsContent value="upcoming">
                {upcomingTasks.length === 0 ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Keine dringenden ISO 27001 Aufgaben in diesem Quartal. Überprüfen Sie regelmäßig die Compliance.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {upcomingTasks.map((task, index) => (
                      <div 
                        key={index}
                        className={`grid grid-cols-[auto_1fr] items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors sm:flex sm:gap-4 ${
                          completedTasks[task.title]?.completed ? 'bg-muted/50 opacity-60' : ''
                        }`}
                      >
                        <Checkbox
                          id={`task-${index}`}
                          checked={completedTasks[task.title]?.completed || false}
                          onCheckedChange={() => toggleTaskCompletion(task.title)}
                          className="mt-1"
                        />
                        <div className={`hidden p-2 rounded-lg flex-shrink-0 sm:block ${
                          task.priority === 'high' ? 'bg-red-500/10' :
                          task.priority === 'medium' ? 'bg-yellow-500/10' : 'bg-blue-500/10'
                        }`}>
                          <Clock className={`w-5 h-5 ${
                            task.priority === 'high' ? 'text-red-600' :
                            task.priority === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col items-start gap-2 mb-1 sm:flex-row sm:justify-between">
                            <label 
                              htmlFor={`task-${index}`}
                              className={`font-semibold cursor-pointer ${
                                completedTasks[task.title]?.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                              }`}
                            >
                              {task.title}
                            </label>
                            <Badge variant={
                              task.priority === 'high' ? 'destructive' :
                              task.priority === 'medium' ? 'default' : 'secondary'
                            } className="text-xs">
                              {task.priority === 'high' ? 'Hoch' :
                               task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {task.description}
                          </p>
                           <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <span>📅 {task.month}</span>
                            <span>🔄 {task.frequency}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Historie */}
              <TabsContent value="history">
                {completedHistory.length === 0 ? (
                  <Alert>
                    <History className="h-4 w-4" />
                    <AlertDescription>
                      Noch keine erledigten Aufgaben. Markieren Sie Aufgaben als erledigt, um sie hier zu sehen.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {completedHistory.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-4 p-4 border rounded-lg bg-muted/30"
                      >
                        <div className="p-2 rounded-lg bg-green-500/10 flex-shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-1">
                            {item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Erledigt am {format(item.date, "dd. MMMM yyyy 'um' HH:mm 'Uhr'", { locale: de })}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-300">
                          Erledigt
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            <Alert className="mt-4 bg-muted/30">
              <Shield className="h-4 w-4" />
              <AlertDescription>
                <strong>ISO 27001 READY Status:</strong> Das System erfüllt grundlegende Anforderungen. 
                Für vollständige Zertifizierung kontaktieren Sie einen akkreditierten Auditor.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Admin Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {adminSections.map((section) => {
            const Icon = section.icon;
            return (
              <Link key={section.to} to={section.to}>
                <Card className="h-full hover:border-primary transition-colors cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <Icon className="h-6 w-6 text-primary" />
                      <CardTitle>{section.title}</CardTitle>
                    </div>
                    <CardDescription>{section.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Klicken Sie hier, um {section.title.toLowerCase()} zu öffnen
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
